import { ItecGridColsDef as GridColsDef, ItecColFilters, ItecDataGrid, useItecDataGrid } from 'src/lib/itaptec/datagrid/src'
import { UserDTOFields, UserFilterDTOFields, UserViewDTO, UserViewDTOFields } from 'src/types/user/userData'

// import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid'
import CachedIcon from '@mui/icons-material/Cached'
import GroupsIcon from '@mui/icons-material/Groups'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import { Button, Card, CardHeader, Dialog, IconButton, Stack, Tooltip } from '@mui/material'
import { GridActionsCellItem, GridSearchIcon } from '@mui/x-data-grid'
import { useState } from 'react'
import toast from 'react-hot-toast'
import BlockUserIcon from 'src/components/icons/BlockUserIcon'
import UnblockUserIcon from 'src/components/icons/UnblockUserIcon'
import { useConfirmationDialog } from 'src/hooks/useConfirmationDialog'
import { HttpUser } from 'src/http/user/httpUser'
import { EntityWithIdFields, schemaSelectionOption } from 'src/types'
import { daysToNaturalLanguage } from 'src/utils/daysToNaturalLanguage'
import { z } from "zod"
import { HttpCacheUser } from "../../../../../http/cache/user/httpCacheUser"
import { ICON_MAS } from "../../../../sistema/components/SharedIcons"
import { WrapperTable } from '../components'
import EditUser from './components/EditUser'
import EditUserGroups from './components/EditUserGroups'

const COL_NAMES = {
  Id: EntityWithIdFields.Id,
  Cargo: UserViewDTOFields.UserPositionDesc,
  Usuario: 'nombre_apellido',
  Email: UserDTOFields.Email,
  UltimoAcceso: UserViewDTOFields.LastLoginDate,
  Activo: UserViewDTOFields.Active,
  Bloqueado: UserViewDTOFields.Blocked
} as const


const schemaFiltroUsuarios = z.object({
  [UserViewDTOFields.Active]: z.array(schemaSelectionOption).optional(),
  [UserViewDTOFields.Blocked]: z.array(schemaSelectionOption).optional(),
  [UserDTOFields.Email]: z.string().optional(),
  [UserFilterDTOFields.UserDescription]: z.string().optional(),
  [UserDTOFields.UserPositionCode]: schemaSelectionOption.optional(),
  [UserFilterDTOFields.LastLoginDateStart]: z.date().optional(),
  [UserFilterDTOFields.LastLoginDateEnd]: z.date().optional(),

  // [EtapaRequestFields.Nombre]: z.string().optional()
})

type SchemaFiltroUsuariosType = z.infer<typeof schemaFiltroUsuarios>

const opcionActivo = {value: 1, label: "Activos", color: "success"}
const opcionTodos = {value: 2, label: "Todos", color: "info"}
const opcionBloqueado = {value: 1, label: "Bloqueados", color: "error"}
const opcionTodosBloqueado = {value: 2, label: "Todos", color: "info"}

const toggleActivoOptions: any[] = [
  opcionActivo,
  opcionTodos,
]

const toggleBloqueadoOptions: any[] = [
  opcionBloqueado,
  opcionTodosBloqueado,
]

const filtros: ItecColFilters<SchemaFiltroUsuariosType> = {
  [COL_NAMES.Usuario]: {
    type: "text",
    name: UserFilterDTOFields.UserDescription,
    props: {
      debounceTime: 500,
      size: "small",
      label: "Usuario",
      autoFocus: true,
    },
  },
  [COL_NAMES.Email]: {
    type: "text",
    name: UserDTOFields.Email,
    props: {
      debounceTime: 500,
      size: "small",
      label: "Email",
      autoFocus: true,
    }

  },

  [COL_NAMES.UltimoAcceso]: {
    type: "date-range",
    name: UserFilterDTOFields.LastLoginDateStart,
    props: {
      startDateName: UserFilterDTOFields.LastLoginDateStart,
      endDateName: UserFilterDTOFields.LastLoginDateEnd,
    }

  },

  [COL_NAMES.Activo]: {
    type: "multiple-button-group",
    name: UserViewDTOFields.Active,
    props: {
      options: toggleActivoOptions,
      enforceValueSet: false,
      size: "small",
      toggleButtonProps: (option: any) => ({
        color: option.color,
      }),
    },
  },

  [COL_NAMES.Bloqueado]: {
    type: "multiple-button-group",
    name: UserViewDTOFields.Blocked,
    props: {
      options: toggleBloqueadoOptions,
      enforceValueSet: false,
      size: "small",
      toggleButtonProps: (option: any) => ({
        color: option.color,
      }),
    },
  },
}

function TableUsers() {
  const {askConfirmation} = useConfirmationDialog()

  const columnas: GridColsDef<UserViewDTO>[] = [
    // {
    //     headerName: 'Id',
    //     field: COL_NAMES.Id,
    //     type: 'number',
    //     width: 70
    // },
    {
      headerName: 'Usuario',
      field: COL_NAMES.Usuario,
      valueGetter: ({row}) => `${row[UserDTOFields.FirstName]} ${row[UserDTOFields.LastName]}`,
      flex: 4
    },
    {
      headerName: 'Email',
      field: COL_NAMES.Email,
      flex: 3
    },

    // {
    //     headerName: 'Usuario',
    //     field: 'nombre_apellido_mail',
    //     renderCell: ({ row }) => {
    //         return (
    //             <Stack>
    //                 <Typography variant='inherit'>
    //                     {`${row[UserDTOFields.FirstName]} ${row[UserDTOFields.LastName]}`}
    //                 </Typography>
    //                 <Typography variant='body2'>
    //                     {row[UserDTOFields.Email]}
    //                 </Typography>
    //             </Stack>
    //         )
    //     },
    //     flex: 2
    // },
    {
      headerName: 'Último acceso',
      field: COL_NAMES.UltimoAcceso,
      valueFormatter: ({value}) => value ? daysToNaturalLanguage(new Date(value)) : '',
      valueGetter: ({value}) => value ? new Date(value) : null,
      type: 'date',
      flex: 2
    },
    {
      headerName: 'Activo',
      field: COL_NAMES.Activo,
      type: 'boolean',
      flex: 0
    },
    {
      headerName: 'Bloqueado',
      field: COL_NAMES.Bloqueado,
      type: 'boolean',
      flex: 0
    },
    {
      field: 'actions',
      type: 'actions',
      getActions: ({row}) => getActions(row, true)
    }
  ]

  const [selectedUserEdit, setSelectedUserEdit] = useState<UserViewDTO>()
  const [selectedUserGroups, setSelectedUserGroups] = useState<UserViewDTO>()

  const getActions = (user: UserViewDTO, small: boolean) => [
    user[UserViewDTOFields.Blocked] ? (
      <GridActionsCellItem
        key={2}
        onClick={() =>
          askConfirmation(() => {
            const promise = HttpUser.unBlockUser(user.id)
            toast
              .promise(promise, {
                success: 'Habilitado',
                error: 'No se pudo habilitar',
                loading: 'Habilitando'
              })
              .then(() => refetch())
          })
        }
        icon={
          <Tooltip title='Desbloquear'>
            <UnblockUserIcon color='success'/>
          </Tooltip>
        }
        label='Desbloquear'
        showInMenu={small}
      />
    ) : (
      <GridActionsCellItem
        key={2}
        onClick={() =>
          askConfirmation(() => {
            const promise = HttpUser.blockUser(user.id)
            toast
              .promise(promise, {
                success: 'Bloqueado',
                error: 'No se pudo bloquear',
                loading: 'Bloqueando'
              })
              .then(() => refetch())
          })
        }
        icon={
          <Tooltip title='Bloquear'>
            <BlockUserIcon color='error'/>
          </Tooltip>
        }
        label='Bloquear'
        showInMenu={small}
      />
    ),
    user[UserViewDTOFields.Active] ? (
      <GridActionsCellItem
        key={3}
        onClick={() =>
          askConfirmation(() => {
            const promise = HttpUser.deactivateUser(user.id)

            toast
              .promise(promise, {
                success: 'Inactivado',
                error: 'No se pudo inactivar',
                loading: 'Inactivando'
              })
              .then(() => refetch())
          })
        }
        icon={
          <Tooltip title='Inactivar'>
            <PersonRemoveIcon color='error'/>
          </Tooltip>
        }
        label='Inactivar'
        showInMenu={small}
      />
    ) : (
      <GridActionsCellItem
        key={3}
        onClick={() =>
          askConfirmation(() => {
            const promise = HttpUser.activateUser(user.id)

            toast
              .promise(promise, {
                success: 'Activado',
                error: 'No se pudo activar',
                loading: 'Activando'
              })
              .then(() => refetch())
          })
        }
        icon={
          <Tooltip title='Activar'>
            <PersonAddIcon color='success'/>
          </Tooltip>
        }
        label='Activar'
        showInMenu={small}
      />
    ),
    <GridActionsCellItem
      key={1}
      onClick={() => {
        setSelectedUserEdit(user)
      }}
      icon={
        <Tooltip title='Ver / Editar'>
          <GridSearchIcon color='info'/>
        </Tooltip>
      }
      label='Ver / Editar'
      showInMenu={small}
    />,
    <GridActionsCellItem
      key={4}
      onClick={() => {
        setSelectedUserGroups(user)
      }}
      icon={
        <Tooltip title='Grupos'>
          <GroupsIcon color='primary'/>
        </Tooltip>
      }
      label='Grupos'
      showInMenu={small}
    />
  ]

  // const opcionUsuarioActivo = (filter: SchemaFiltroUsuariosType, field: keyof SchemaFiltroUsuariosType) => {
  //   if (filter?.[field]?.includes(opcionActivo) && filter?.[field]?.includes(opcionInactivo)) {
  //     return undefined;
  //   } else if (filter?.[field]?.includes(opcionActivo)) {
  //     return true;
  //   } else if (filter?.[field]?.includes(opcionInactivo)) {
  //     return false;
  //   }
  // }

  const opcionUsuarioBloqueado = (filter: SchemaFiltroUsuariosType, field: keyof SchemaFiltroUsuariosType) => {
    const fieldValue = filter?.[field];
    if (Array.isArray(fieldValue) && fieldValue.length === 1) {
      if (fieldValue[0].value === opcionBloqueado.value) return true
      if (fieldValue[0].value === opcionTodosBloqueado.value) return undefined
    }
    return undefined
  }

  const opcionUsuarioActivo = (filter: SchemaFiltroUsuariosType, field: keyof SchemaFiltroUsuariosType) => {
    const fieldValue = filter?.[field];
    if (Array.isArray(fieldValue) && fieldValue.length === 1) {
      if (fieldValue[0].value === opcionActivo.value) return true
      if (fieldValue[0].value === opcionTodos.value) return undefined
    }
    return undefined
  }

  const {
    datagridProps,
    control: {
      query: {refetch}
    }
  } = useItecDataGrid({
    pagination: true,
    columns: columnas,
    getEntitiesPaginated: (pagination, filter) =>
      HttpUser.getUserList(pagination,
        filter ? {
            [UserViewDTOFields.Active]: opcionUsuarioActivo(filter, UserViewDTOFields.Active),
            [UserDTOFields.Email]: filter[UserDTOFields.Email],
            [UserFilterDTOFields.UserDescription]: filter[UserFilterDTOFields.UserDescription],
            [UserDTOFields.UserPositionCode]: filter[UserDTOFields.UserPositionCode]?.value,
            [UserFilterDTOFields.LastLoginDateStart]: filter[UserFilterDTOFields.LastLoginDateStart],
            [UserFilterDTOFields.LastLoginDateEnd]: filter[UserFilterDTOFields.LastLoginDateEnd],
            [UserViewDTOFields.Blocked]: opcionUsuarioBloqueado(filter, UserViewDTOFields.Blocked),
          }
          : {}),
    density: 'compact',
    filters: filtros
  })

  return (
    <WrapperTable>
      <Card>
        <CardHeader
          title='Administración de usuarios'
          action={
            <Stack direction={'row'}>
              <IconButton disabled={datagridProps.loading} onClick={() => refetch()}>
                <CachedIcon/>
              </IconButton>


              <Button variant="contained" color="primary" endIcon={ICON_MAS} onClick={() => {
                setSelectedUserEdit({
                  [EntityWithIdFields.Id]: 0,
                } as UserViewDTO)
              }}>
                Nuevo
              </Button>

            </Stack>

          }
        />
        <ItecDataGrid {...datagridProps} />
      </Card>
      <>
        <Dialog open={!!selectedUserEdit} fullWidth maxWidth='sm' keepMounted={false}>
          {selectedUserEdit && (
            <EditUser
              mode={selectedUserEdit.id === 0 ? 'edit' : 'view'}
              onClose={() => {
                setSelectedUserEdit(undefined)
                refetch()
              }}
              user={selectedUserEdit}
            />
          )}
        </Dialog>
        <Dialog open={!!selectedUserGroups} fullWidth maxWidth='lg' keepMounted={false}>
          {selectedUserGroups && (
            <EditUserGroups
              onClose={() => {
                setSelectedUserGroups(undefined)
                refetch()
              }}
              user={selectedUserGroups}
            />
          )}
        </Dialog>
      </>
    </WrapperTable>
  )
}

export default TableUsers
